import { IndicatorVariant } from 'Components/Indicator/Indicator';
import { getOperationalControlIdentifierString } from 'Helpers/ControlFormatter/ControlFormatter';

import { AiTprmServiceAssessment } from './ArtificialIntelligence';
import { ThirdPartyExceptionResponse } from './Exceptions';
import { FileToBeUploaded, FileUpdates, UploadedFile } from './Files';
import { ThirdPartyIssueResponse } from './Issues';
import { ControlText, Effectiveness } from './OperationalControls';
import { ScheduleFrequency, ScheduleFrequencyKeys } from './ScheduleFrequency';
import { DisplayableTag } from './Tags';
import { GroupOptionType, OptionType } from './Types/GlobalType';

export interface AssessmentWorkflowDataForNonTprmUser {
    ddq_has_been_marked_complete_by_third_party: boolean;
    state: ServiceAssessmentState;
}

export interface AssessmentWorkflowSetupForNonTprmUser {
    due_date?: string;
    irq_target_completion_date?: string;
}

export interface ServiceForNonTprmUser {
    finalization_state: FinalizationState;
    assessment_workflow_data: AssessmentWorkflowDataForNonTprmUser;
    assessment_workflow_setup: AssessmentWorkflowSetupForNonTprmUser;
    created_time: string;
    delegates: string[]; // List of optional user IDs to receive Notifications in addition to the Vendor Service Manager.
    description: string;
    id: string;
    name: string;
    tags: string[];
    vendor_contacts: ThirdPartyContact[]; // This property is called "vendor_contacts" because of the `ThirdPartyContact` interface, but note that these are the contacts for the _service_. This is not the same data as `ThirdParty.vendor_contacts`.
    vendor_id: string;
    vendor_name: string;
    vendor_service_manager_user_id: string;

    created_by?: string; // Optional because of legacy data. All new services have a value for this field.
    inherent_risk_score?: number;
    last_assessed?: string;
    residual_risk_score?: number;
    responsible_organization?: string;
}

export interface ThirdPartyWithServicesForNonTprmUser {
    finalization_state: FinalizationState;
    country: string;
    created_time: string;
    id: string;
    name: string;
    services: ServiceForNonTprmUser[];
    tags: string[];
    vendor_contacts: ThirdPartyContact[];
    vendor_manager_user_id: string;

    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    created_by?: string; // Optional because of legacy data. All new third parties have a value for this field.
    ein?: string;
    state?: string;
    website?: string;
    zip_code?: string;
}

// Note: It would likely be better if "inactive" were not a member of this enum, and instead, `undefined` represented absent risk ratings. It's confusing and error-prone that we can equate ratings to numbers _except_ for when a rating is "inactive": for example, special care has to be taken in some places to completely ignore `0` ratings, such as when the backend calculates averages of "active" services for Dashboards. If it's easy to change this in the future, go for it; otherwise, it doesn't seem worth the time/effort.
export enum RiskRating {
    INACTIVE = 0,
    LOW,
    LOW_MODERATE,
    MODERATE,
    MODERATE_HIGH,
    HIGH,
}

export enum FinalizationState {
    DRAFT_IN_PROGRESS = 'Draft (in progress)',
    DRAFT_SUBMITTED_TO_BE_FINALIZED = 'Draft (submitted to be finalized)',
    FINALIZED = 'Finalized',
}

/**
 * Allows the user to set the IRQ target completion date when starting OR restarting the assessment workflow.
 */
export interface StartAssessmentWorkflowRequest {
    irq_target_completion_date?: string;
}

/**
 Each field represents the maximum value for the range corresponding to an inherent risk score.
The end ranges ("low" and "high") can be known only within the context of the points assigned to answers: the lowest possible score for an IRQ is "low"'s minimum value; the highest possible score for an IRQ is "high"'s maximum value.

For example, if the values on this object are...
    low: 10
    low_moderate: 20
    moderate: 30
    moderate_high: 40

...and the lowest possible IRQ score is -10, and the highest possible IRQ score is 50...

...then the ranges for inherent risk scores are as follows:
    low: -10 to 10
    low_moderate: 11 to 20
    moderate: 21 to 30
    moderate_high: 31 to 40
    high: 41 to 50
 */
export interface RiskRatingPointThresholds {
    low: number;
    low_moderate: number;
    moderate: number;
    moderate_high: number;
}

export interface InherentRiskQuestionnaireSection {
    is_applicable: boolean;
    name: string;
    questions: InherentRiskQuestionnaireQuestion[];
    files: UploadedFile[];
    text?: string;
}

export interface InherentRiskQuestionnaireQuestion {
    text: string;
    answers: InherentRiskQuestionnaireAnswer[];
    selected_answer_index?: number;
}

export interface InherentRiskQuestionnaireAnswer {
    text: string;
    points: number;
}

export interface InherentRiskQuestionnaireSectionConfiguration {
    name: string;
    questions: InherentRiskQuestionnaireQuestionConfiguration[];
}

export interface InherentRiskQuestionnaireQuestionConfiguration {
    text: string;
    answers: InherentRiskQuestionnaireAnswerConfiguration[];
}

export interface InherentRiskQuestionnaireAnswerConfiguration {
    text: string;
    points: number;
}

export interface InherentRiskQuestionnaireResponse {
    third_party_id: string;
    service_id: string;
    has_been_submitted: boolean;
    thresholds: RiskRatingPointThresholds;
    sections: InherentRiskQuestionnaireSection[];
    inherent_risk_score?: 1 | 2 | 3 | 4 | 5;
}

export interface InherentRiskQuestionnaireSectionSelections {
    is_applicable: boolean;
    file_updates?: FileUpdates;
    answers?: (number | null)[];
    text?: string;
}

export interface SaveOrSubmitInherentRiskQuestionnaireRequest {
    submit: boolean;
    inherent_risk_score?: number;
    section_selections: InherentRiskQuestionnaireSectionSelections[];
    third_party_contacts: ThirdPartyContact[];
}

export interface InherentRiskQuestionnaireConfiguration {
    thresholds: RiskRatingPointThresholds;
    sections: InherentRiskQuestionnaireSectionConfiguration[];
}

export interface InherentRiskQuestionnaireProgress {
    numberOfQuestions: number;
    numberOfQuestionsAnswered: number;
}

/**
 * Contact information for Third-Party personnel. Used for both Third Party and Third-Party Service contact information.
 */
export interface ThirdPartyContact {
    name?: string;
    email_address?: string;
    phone_number?: string;
    additional_information?: string;
}

export interface CreateThirdPartyRequest {
    finalization_state: FinalizationState;
    country: string;
    name: string;
    vendor_manager_user_id: string;
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    ein?: string;
    state?: string;
    tags: string[];
    vendor_contacts: ThirdPartyContact[];
    website?: string;
    zip_code?: string;
}

export interface UpdateThirdPartyRequest {
    country?: string;
    name?: string;
    vendor_manager_user_id?: string;
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    ein?: string;
    state?: string;
    tags?: string[];
    vendor_contacts?: ThirdPartyContact[];
    website?: string;
    zip_code?: string;
}

export interface ThirdParty {
    finalization_state: FinalizationState;
    country: string;
    created_time: string;
    id: string;
    name: string;
    tags: string[]; // Optional from a user perspective, but not from a code perspective since this will always at a minimum be an empty list.
    vendor_contacts: ThirdPartyContact[]; // Optional from a user perspective, but not from a code perspective since this will always at a minimum be an empty list.
    vendor_manager_user_id: string;

    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    created_by?: string; // Optional because of legacy data. All new third parties have a value for this field.
    ein?: string;
    finalized_by?: string; // Optional because of legacy data. All new services have a value for this field.
    finalized_time?: string; // Optional because of legacy data. All new services have a value for this field.
    state?: string;
    website?: string;
    zip_code?: string;
}

/**
 * Note that this is equivalent to the `ThirdParty` interface, but it is still defined separately as to avoid someone inadvertently adding sensitive information to `ThirdParty` that will not actually be returned to the frontend for non-TPRM users.
 */
export interface ThirdPartyForNonTprmUser {
    finalization_state: FinalizationState;
    country: string;
    created_time: string;
    id: string;
    name: string;
    tags: string[];
    vendor_contacts: ThirdPartyContact[];
    vendor_manager_user_id: string;

    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    created_by?: string; // Optional because of legacy data. All new third parties have a value for this field.
    ein?: string;
    state?: string;
    website?: string;
    zip_code?: string;
}

export interface ThirdPartyResponse extends ThirdParty {
    issues: ThirdPartyIssueResponse[];
    exceptions: ThirdPartyExceptionResponse[];
    [key: string]: any; // TODO: Remove.
}

// TODO: Can we consolidate this with ThirdPartyResponse?
export interface ThirdPartyResponseWithServices extends ThirdPartyResponse {
    services: ThirdPartyService[];
    [key: string]: any; // TODO: Remove.
}

// As defined by the backend, Third-Party archives have all the same properties as a Third-Party, except that Tags are in a displayable format.
// (Historical Tags may not match up with the Client's currently configured Tags, so they can't be looked up in the same way as Tags on "current" items can be.)
export type ThirdPartyArchive = Omit<ThirdParty, 'tags'> & { tags: DisplayableTag[] };

export type ThirdPartyIdToNameMap = Map<string, string>;

/**
 * Used to create or update a Third-Party Service.
 * IMPORTANT: If a new property is added to this interface, be sure to include it EVERYWHERE the request is built. Inadvertently omitting a property will lead to a bug where saving the service will clear the value of the omitted property.
 * TODO: Use a pattern that will avoid the landmine described above.
 */
export interface UpdateServiceRequest {
    name: string;
    description: string;
    vendor_service_manager_user_id: string; // TODO: Due to the ThirdPartyResponse building off ThirdParty, the attributes need to line up until the migration
    assessment_due_date?: string | null; // Sending `null` removes the due date.
    delegates?: string[];
    irq_target_completion_date?: string; // Sending `undefined` removes the target date.
    responsible_organization?: string;
    tags?: string[]; // TODO: See above
    vendor_contacts?: ThirdPartyContact[]; // TODO: See above
}

export interface CreateServiceRequest extends UpdateServiceRequest {
    finalization_state: FinalizationState;
    irq_target_completion_date?: string;
}

export interface UpdateServiceAssessmentRequest {
    is_submit: boolean;
    file_updates: FileUpdates;
    new_assessment_documents?: FileToBeUploaded[];
    assessment_control_effectiveness?: Effectiveness;
    assessment_residual_risk_score?: number;
    assessment_additional_information?: string;
    new_assessment_due_date?: string;
}

export interface UpdateCommonAssessmentRequest {
    parent_identifier: ServiceIdentifier;
    child_identifier: ServiceIdentifier;
    linked: boolean;
}

/**
 * AWAITING_ASSESSMENT -> EVALUATING_INHERENT_RISK when the user clicks the button in the UI to start a new assessment workflow. At this time, a new IRQ is generated (answers are preserved where possible).
 * EVALUATING_INHERENT_RISK -> PERFORMING_DUE_DILIGENCE when the user submits the IRQ. At this time, a new DDQ is generated (answers are preserved where possible).
 * PERFORMING_DUE_DILIGENCE -> AWAITING_ASSESSMENT when the user submits the Final Review. At this time, the service / its workflow are copied into an "archive" object to be shown in assessment reports.
 */
export enum ServiceAssessmentState {
    AWAITING_ASSESSMENT = 'Awaiting Assessment',
    EVALUATING_INHERENT_RISK = 'Evaluating Inherent Risk',
    PERFORMING_DUE_DILIGENCE = 'Performing Due Diligence',
}

export interface AssessmentWorkflowData {
    state: ServiceAssessmentState;

    irq_completed_time?: string;
    irq_inherent_risk_score?: number;
    irq_started_time?: string;

    ddq_completed_time?: string; // The time at which the third party marked the DDQ as complete, if the DDQ is marked complete.

    final_review_additional_information?: string;
    final_review_control_effectiveness?: number;
    final_review_documents: UploadedFile[];
    final_review_residual_risk_score?: number;
}

export interface AssessmentWorkflowSetup {
    common_assessment_children: ServiceSummary[];
    common_assessment_parent?: ServiceSummary;
    irq_target_completion_date?: string;
    due_date?: string;
    schedule?: Schedule;
}

export interface ServiceSummary {
    id: string;
    vendor_id: string;
    name: string;
}

// DANGER: This interface is used incorrectly by `DueDiligenceQuestionnaire`. If you change _anything_ on this interface, you _might_ break the DDQ. (Specifically, the third party's usage of the DDQ. The client's usage of the Control Assessment will probably be okay). If you're unlucky enough to need to change something here, compare this with `PublicService` and look at how `DueDiligenceQuestionnaire` might be affected.
// TODO: Rename this to ThirdPartyService or something like that.
export interface ThirdPartyService {
    finalization_state: FinalizationState;
    assessment_workflow_data: AssessmentWorkflowData;
    assessment_workflow_setup: AssessmentWorkflowSetup;
    created_time: string;
    description: string;
    delegates: string[]; // List of optional user IDs to receive Notifications in addition to the Vendor Service Manager.
    id: string;
    name: string;
    responsible_organization?: string;
    tags: string[]; // Optional from a user perspective, but not from a code perspective since this will always at a minimum be an empty list.
    vendor_contacts: ThirdPartyContact[]; // This property is called "vendor_contacts" because of the `ThirdPartyContact` interface, but note that these are the contacts for the _service_. This is not the same data as `ThirdParty.vendor_contacts`.
    vendor_id: string;
    vendor_name: string;
    vendor_service_manager_user_id: string;

    created_by?: string; // Optional because of legacy data. All new services have a value for this field.
    finalized_by?: string; // Optional because of legacy data. All new services have a value for this field.
    finalized_time?: string; // Optional because of legacy data. All new services have a value for this field.
    last_assessed?: string;
    inherent_risk_score?: number;
    control_effectiveness?: number;
    residual_risk_score?: number;
}

// As defined by the backend, Third-Party Service archives have all the same properties as a Third-Party Service, except that Tags are in a displayable format.
// (Historical Tags may not match up with the Client's currently configured Tags, so they can't be looked up in the same way as Tags on "current" items can be.)
export type ThirdPartyServiceArchive = Omit<ThirdPartyService, 'tags'> & { tags: DisplayableTag[] };

export interface ServiceIdentifier {
    vendor_id: string;
    service_id: string;
}

export const numberAsRiskScore = (riskScore: number): RiskRating => {
    if (riskScore === 0) {
        return RiskRating.INACTIVE;
    } else if (riskScore >= 1 && riskScore < 2) {
        return RiskRating.LOW;
    } else if (riskScore >= 2 && riskScore < 3) {
        return RiskRating.LOW_MODERATE;
    } else if (riskScore >= 3 && riskScore < 4) {
        return RiskRating.MODERATE;
    } else if (riskScore >= 4 && riskScore < 5) {
        return RiskRating.MODERATE_HIGH;
    } else if (riskScore === 5) {
        return RiskRating.HIGH;
    }

    return RiskRating.INACTIVE;
};

export const riskRatingAsString = (riskRating: RiskRating): string => {
    switch (riskRating) {
        case RiskRating.INACTIVE:
            return 'Inactive';
        case RiskRating.HIGH:
            return 'High';
        case RiskRating.MODERATE_HIGH:
            return 'Moderate/High';
        case RiskRating.MODERATE:
            return 'Moderate';
        case RiskRating.LOW_MODERATE:
            return 'Low/Moderate';
        case RiskRating.LOW:
            return 'Low';
    }
};

export const numberAsRiskRatingString = (riskRating: number): string => {
    return riskRatingAsString(numberAsRiskScore(riskRating));
};

export const riskRatingLabelAsNumber = (riskRatingLabel: string) => {
    switch (riskRatingLabel) {
        case 'Inactive':
            return RiskRating.INACTIVE;
        case 'High':
            return RiskRating.HIGH;
        case 'Moderate/High':
            return RiskRating.MODERATE_HIGH;
        case 'Moderate':
            return RiskRating.MODERATE;
        case 'Low/Moderate':
            return RiskRating.LOW_MODERATE;
        case 'Low':
            return RiskRating.LOW;
    }
};

const INHERENT_RISK_GROUP_ID = 'inherent_risk_score';
const RESIDUAL_RISK_GROUP_ID = 'residual_risk_score';

export const InherentRiskFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: Inactive',
        value: RiskRating.INACTIVE,
    },
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: High',
        value: RiskRating.HIGH,
    },
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: Moderate/High',
        value: RiskRating.MODERATE_HIGH,
    },
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: Moderate',
        value: RiskRating.MODERATE,
    },
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: Low/Moderate',
        value: RiskRating.LOW_MODERATE,
    },
    {
        groupId: INHERENT_RISK_GROUP_ID,
        label: 'Inherent Risk: Low',
        value: RiskRating.LOW,
    },
];

export const ResidualRiskFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: Inactive',
        value: RiskRating.INACTIVE,
    },
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: High',
        value: RiskRating.HIGH,
    },
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: Moderate/High',
        value: RiskRating.MODERATE_HIGH,
    },
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: Moderate',
        value: RiskRating.MODERATE,
    },
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: Low/Moderate',
        value: RiskRating.LOW_MODERATE,
    },
    {
        groupId: RESIDUAL_RISK_GROUP_ID,
        label: 'Residual Risk: Low',
        value: RiskRating.LOW,
    },
];

export const RiskRatingSelectOptions: OptionType[] = [
    {
        label: 'Low',
        value: RiskRating.LOW,
    },
    {
        label: 'Low/Moderate',
        value: RiskRating.LOW_MODERATE,
    },
    {
        label: 'Moderate',
        value: RiskRating.MODERATE,
    },
    {
        label: 'Moderate/High',
        value: RiskRating.MODERATE_HIGH,
    },
    {
        label: 'High',
        value: RiskRating.HIGH,
    },
];

export const getRiskRatingVariantColor = (riskRating: RiskRating): IndicatorVariant => {
    switch (riskRating) {
        case RiskRating.INACTIVE:
            return IndicatorVariant.GRAY;
        case RiskRating.LOW:
            return IndicatorVariant.DARK_GREEN;
        case RiskRating.LOW_MODERATE:
            return IndicatorVariant.LIGHT_GREEN;
        case RiskRating.MODERATE:
            return IndicatorVariant.YELLOW;
        case RiskRating.MODERATE_HIGH:
            return IndicatorVariant.ORANGE;
        case RiskRating.HIGH:
            return IndicatorVariant.RED;
    }
};

// Questionnaire Configuration
export interface QuestionnaireConfigurationResponse {
    control_frameworks: ControlFrameworkConfiguration[];
}
export interface ControlFrameworkConfiguration {
    control_framework: string;
    control_framework_name: string;
    control_groups: ControlGroupConfiguration[];
    is_custom: boolean;
}

export interface ControlGroupConfiguration {
    control_framework: string;
    control_group_id: string;
    controls: ControlConfiguration[];
    control_group_name: string;
    control_group_description?: string;
    is_custom: boolean;
}

export interface ControlConfiguration {
    control_framework: string;
    control_group_id: string;
    control_id: string;
    control_text: ControlText[];
    questions: QuestionConfiguration[];
    control_name?: string;
    is_custom: boolean;
}

export interface QuestionConfiguration {
    _type: QuestionType;
    text: string;
    mapped_risk_ratings: RiskRating[];
    options?: string[];
}

export enum QuestionType {
    FREEFORM = 'FREEFORM',
    SINGLE_SELECT = 'SINGLE_SELECT',
    MULTIPLE_SELECT = 'MULTIPLE_SELECT',
    DOCUMENT_UPLOAD = 'DOCUMENT_UPLOAD',
}

export interface CreateControlConfigurationRequest {
    control_text: ControlText[];
    control_name: string;
}

export interface UpdateControlConfigurationRequest {
    control_text?: ControlText[];
    control_name?: string;
    questions?: QuestionConfiguration[];
}

export interface CreateControlGroupConfigurationRequest {
    control_group_name: string;
    control_group_description: string;
}

export interface UpdateControlGroupConfigurationRequest {
    control_group_name?: string;
    control_group_description?: string;
}

export interface CreateControlFrameworkConfigurationRequest {
    control_framework_name: string;
}

export interface UpdateControlFrameworkConfigurationRequest {
    control_framework_name: string;
}

export type UpdateConfigurationRequest = UpdateControlFrameworkConfigurationRequest | UpdateControlGroupConfigurationRequest | UpdateControlConfigurationRequest;

export const identifierMappedToRequest = (request: UpdateConfigurationRequest, framework: string, groupId?: string, controlId?: string): [string, UpdateConfigurationRequest] => {
    return [getOperationalControlIdentifierString(framework, groupId, controlId), request];
};

// Questionnaire Instance
export interface QuestionnaireGroupControlsResponse {
    controls: ControlInstanceResponse[];
}

export interface ControlFrameworkInstanceResponse {
    control_framework: string;
    control_framework_name: string;
    control_groups: ControlGroupInstanceResponse[];
    control_assessments_completed: number;
    questions_completed: number;
    is_custom: boolean;
}

export interface ControlGroupInstanceResponse {
    control_framework: string;
    control_group_id: string;
    control_group_name: string;
    number_of_controls: number;
    control_assessments_completed: number;
    number_of_questions: number;
    questions_completed: number;
    control_group_description?: string;
    is_custom: boolean;
}

export interface ControlInstanceResponse {
    control_framework: string;
    control_group_id: string;
    control_id: string;
    control_group_name: string;
    control_text: ControlText[];
    questions: QuestionTypes[];
    is_custom: boolean;
    control_name?: string;
    control_assessment_effectiveness?: Effectiveness;
    control_assessment_comment?: string;
    control_assessment_documents: UploadedFile[];
}

export interface QuestionInstance {
    id: string;
    control_identifier: string;
    text: string;
    answer_text?: string;
    answer_documents: UploadedFile[];
}

export interface SingleSelectQuestionInstance extends QuestionInstance {
    _type: QuestionType.SINGLE_SELECT;
    options?: string[];
    answer_index?: number;
}

export interface MultipleSelectQuestionInstance extends QuestionInstance {
    _type: QuestionType.MULTIPLE_SELECT;
    options?: string[];
    answer_indexes?: number[];
}

export interface FreeformQuestionInstance extends QuestionInstance {
    _type: QuestionType.FREEFORM;
}

export interface DocumentUploadQuestionInstance extends QuestionInstance {
    _type: QuestionType.DOCUMENT_UPLOAD;
}

export type QuestionTypes = FreeformQuestionInstance | SingleSelectQuestionInstance | MultipleSelectQuestionInstance | DocumentUploadQuestionInstance;

export type Answer = string | number | number[] | undefined;

export interface UpdateControlInstanceRequest {
    updates?: Partial<ControlInstanceResponse>;
    new_documentation?: FileToBeUploaded[];
}

export interface QuestionUpdates {
    updates?: Partial<QuestionTypes>;
    new_documentation?: FileToBeUploaded[];
}

export interface UpdateControlInstanceQuestionsRequest {
    questions_updates?: { [key: string]: QuestionUpdates };
}

interface QuestionnaireAggregationBase {
    status?: QuestionnaireMetadataStatus;
    status_last_modified_timestamp?: string;
}
export interface VendorQuestionnaireAggregationResponse extends QuestionnaireAggregationBase {
    submitted_ddq_discussions?: SubmittedDiscussionVendorResponse[];
}

export interface QuestionnaireAggregationResponse extends QuestionnaireAggregationBase {
    number_of_controls: number;
    number_of_questions: number;
    number_of_control_assessments_completed: number;
    number_of_questions_completed: number;
    control_effectiveness_summary: {
        '0': number;
        '1': number;
        '2': number;
        '3': number;
        '4': number;
        '5': number;
    };
    due_diligence_started_time?: string;
    control_assessment_started_time?: string;
    submitted_ddq_discussions?: SubmittedDiscussionClientResponse[];
}

// Assessment report
export interface QuestionnaireReport {
    average_control_effectiveness: number;
    control_frameworks: ReportControlFrameworkResponse[];
}

export interface ReportControlFrameworkResponse {
    control_framework: string;
    control_framework_name: string;
    control_groups: ReportControlGroupResponse[];
    control_assessments_completed: number;
    questions_completed: number;
    control_framework_effectiveness: number;
    is_custom: boolean;
}
export interface ReportControlGroupResponse {
    control_framework: string;
    control_group_id: string;
    control_group_name: string;
    number_of_controls: number;
    control_assessments_completed: number;
    number_of_questions: number;
    questions_completed: number;
    control_group_description?: string;
    controls: ControlInstanceResponse[];
    control_group_effectiveness: number;
    is_custom: boolean;
}

export const thirdPartyNameComparator = (thirdPartyIdA: string, thirdPartyIdB: string, thirdPartyIdToNameMap: Map<string, string>): number => {
    const thirdPartyNameA = thirdPartyIdToNameMap.get(thirdPartyIdA)!;
    const thirdPartyNameB = thirdPartyIdToNameMap.get(thirdPartyIdB)!;
    return thirdPartyNameA.localeCompare(thirdPartyNameB);
};

export interface PublicDDQRegistrationRequest {
    email_address: string;
    password: string;
    password_confirm: string;
}

/**
 * DEPRECATED. Don't use this anymore. Mixing and matching letter case is confusing. Use ScheduleV2 instead.
 */
export type Schedule = {
    schedule_number: number;
    schedule_frequency: ScheduleFrequencyKeys;
};

// Rename this (remove the V2) once Schedule is eliminated.
export type ScheduleV2 = {
    schedule_number: number;
    schedule_frequency: ScheduleFrequency;
};

export type ServiceAssessmentSchedule = {
    low?: Schedule;
    low_moderate?: Schedule;
    moderate?: Schedule;
    moderate_high?: Schedule;
    high?: Schedule;
};

export interface ThirdPartyServiceAssessmentArchiveMetadata {
    timestamp: string;
    assessment_submitter_id: string;
}

/**
 * Represents an archived Third-Party Service Assessment.
 * @param ai_tprm_service_assessment - Optional because the AI TPRM Service Assessment may not have been run for this Third-Party Service Assessment (and/or the Client is not licensed for it).
 */
export interface ThirdPartyServiceAssessmentArchive {
    control_instances: AssessmentControlInstances[];
    inherent_risk_evidence: InherentRiskQuestionnaireResponse;
    metadata: ThirdPartyServiceAssessmentArchiveMetadata;
    questionnaire_aggregation: QuestionnaireAggregation;
    service: ThirdPartyServiceArchive;
    vendor: ThirdPartyArchive;
    ai_tprm_service_assessment?: AiTprmServiceAssessment;
}

export interface AssessmentControlInstances {
    control_identifier: string;
    control_assessment_documents: UploadedFile[];
    control_text: ControlText[];
    is_custom: boolean;
    questions: QuestionInstance[];
    control_assessment_effectiveness?: number;
}

export interface QuestionnaireAggregation extends QuestionnaireAggregationResponse {
    control_frameworks: { [key: string]: ThirdPartyServiceAssessmentArchiveFramework };
    control_groups: { [key: string]: ThirdPartyServiceAssessmentArchiveGroup };
}

export interface ThirdPartyServiceAssessmentArchiveFramework {
    control_identifier: string;
    is_custom: boolean;
    control_framework_name: string;
    control_framework_effectiveness: number;
}

export interface ThirdPartyServiceAssessmentArchiveGroup {
    control_identifier: string;
    control_group_name: string;
    control_group_effectiveness: number;
    is_custom: boolean;
}

export enum ReportListingTableFilterOptions {
    DATE_CREATED = 'created_time',
    INHERENT_RISK_SCORE = 'inherent_risk_score',
    RESIDUAL_RISK_SCORE = 'residual_risk_score',
    CONTROL_EFFECTIVENESS = 'control_effectiveness',
    REPORT_TITLE = 'reportTitle',
}

export const ControlEffectivenessFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: ReportListingTableFilterOptions.CONTROL_EFFECTIVENESS,
        label: 'Control Effectiveness: Fail',
        value: Effectiveness.FAIL,
    },
    {
        groupId: ReportListingTableFilterOptions.CONTROL_EFFECTIVENESS,
        label: 'Control Effectiveness: Weak',
        value: Effectiveness.WEAK,
    },
    {
        groupId: ReportListingTableFilterOptions.CONTROL_EFFECTIVENESS,
        label: 'Control Effectiveness: Moderate',
        value: Effectiveness.MODERATE,
    },
    {
        groupId: ReportListingTableFilterOptions.CONTROL_EFFECTIVENESS,
        label: 'Control Effectiveness: Strong',
        value: Effectiveness.STRONG,
    },
    {
        groupId: ReportListingTableFilterOptions.CONTROL_EFFECTIVENESS,
        label: 'Control Effectiveness: Robust',
        value: Effectiveness.ROBUST,
    },
];

export interface FolderTypes {
    type_set: string[];
}

export interface CreateOrUpdateFolderRequest {
    name: string;
    type: string;
    third_party_manager_notifications_enabled: boolean;
    effective_date?: string;
    expiration_date?: string;
    comments?: string;
    file_updates?: FileUpdates;
}

export interface FolderVersion {
    id: string;
    name: string;
    type: string;
    vendor_manager_notifications_enabled: boolean;
    created_time: string;
    last_updated: string;
    last_updated_by: string;
    effective_date?: string;
    expiration_date?: string;
    comments?: string;
    files?: UploadedFile[];
    [key: string]: any;
}

export interface FolderAllVersions {
    current_version: FolderVersion;
    past_versions: FolderVersion[];
}

/**
 * Used to (re-)send the DDQ email to the Third-Party Service contact(s).
 * Results in existing Third-Party Service contact credentials being deleted, forcing them to re-register if they already have.
 * Can optionally clear all existing progress on the DDQ and Control Assessment.
 */
export interface ResetAndNotifyThirdPartyRequest {
    reset_ddq: boolean;
    third_party_contacts: ThirdPartyContact[]; // A list of Third-Party Service contacts that will be notified about the Third-Party Questionnaire (DDQ).
}

export enum QuestionnaireMetadataStatus {
    QUESTIONNAIRE_SUBMITTED_BY_THIRD_PARTY = 'Questionnaire Submitted by Third Party',
    DISCUSSION_UPDATED_BY_CLIENT = 'Additional Information Requested by Client',
    DISCUSSION_UPDATED_BY_THIRD_PARTY = 'Additional Information Provided by Third Party',
}

export enum DDQDiscussionType {
    CLIENT = 'Client',
    THIRD_PARTY = 'Third Party',
}

export interface DDQDiscussionDraft {
    _type: DDQDiscussionType;
    comment: string;
    requested_by: string;
    discussion_id: string;
}
export interface DDQDiscussion {
    _type: DDQDiscussionType;
    comment: string;
    discussion_id: string;
}

export interface ClientDDQDiscussionFromClientResponse extends DDQDiscussion {
    _type: DDQDiscussionType.CLIENT;
    requested_by: string; // will be UUID
}

export interface DDQDiscussionFromThirdPartyResponse extends DDQDiscussion {
    _type: DDQDiscussionType.THIRD_PARTY;
    requested_by: string; // Will be email
}

export interface ThirdPartyDDQDiscussionFromClientResponse extends DDQDiscussion {
    _type: DDQDiscussionType.CLIENT; // Vendor will not see client details. Client name used instead
    requested_by?: never;
}

export interface SubmittedDiscussionClientResponse {
    _type: DDQDiscussionType.CLIENT;
    discussions: (ClientDDQDiscussionFromClientResponse | DDQDiscussionFromThirdPartyResponse)[];
    submitted_timestamp: string;
}

export interface SubmittedDiscussionVendorResponse {
    _type: DDQDiscussionType.THIRD_PARTY;
    discussions: (DDQDiscussionFromThirdPartyResponse | ThirdPartyDDQDiscussionFromClientResponse)[];
    submitted_timestamp: string;
}

export interface SubmitDDQDiscussionRequest {
    discussion_ids: string[];
}

export interface CreateDDQDiscussionDraftRequest {
    comment: string;
}
